import React, { useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const TimeDiscrepancyChart = ({ results }) => {
  const { data, isAligned, mismatchDetails } = useMemo(() => {
    const timeSpent = results.questions.find(q => q.questionKey === 'time_spent')?.rankings || [];
    const timeDesired = results.questions.find(q => q.questionKey === 'time_desired')?.rankings || [];
    
    const mappedData = timeSpent.map(current => {
      const desired = timeDesired.find(d => d.item === current.item).score;
      return {
        activity: current.item.length > 30 ? current.item.substring(0, 30) + '...' : current.item,
        fullActivity: current.item,
        current: current.score,
        desired: desired,
        gap: desired - current.score
      };
    })
    .sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap))  // Sort by absolute gap value
    .slice(0, 5);  // Take only top 5 items

    const isAligned = mappedData.every(item => Math.abs(item.gap) <= 0.05);

    return {
      data: mappedData.map(item => ({
        ...item,
        current: item.current * 100,
        desired: item.desired * 100,
        gap: item.gap * 100
      })),
      isAligned,
      mismatchDetails: mappedData
    };
  }, [results]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    
    return (
      <Box sx={{ 
        bgcolor: 'background.paper', 
        p: 2, 
        border: '1px solid #ccc',
        borderRadius: 1,
        boxShadow: 1
      }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {payload[0]?.payload.fullActivity}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Temps Actuel: {payload[0]?.value.toFixed(1)}%
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Temps Souhaité: {payload[0]?.payload.desired.toFixed(1)}%
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: payload[0]?.payload.gap > 0 ? 'success.main' : 'error.main',
            fontWeight: 'bold',
            mt: 1
          }}
        >
          Écart: {payload[0]?.payload.gap.toFixed(1)}%
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography variant="h6" gutterBottom align="center">
        Top 5 des Écarts Temps Actuel vs Souhaité
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number" 
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
            />
            <YAxis 
              type="category" 
              dataKey="activity" 
              width={140}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <ReferenceLine x={0} stroke="#666" />
            <Bar 
              dataKey="current" 
              name="Temps Actuel" 
              fill="#8884d8" 
              barSize={20}
            />
            <Bar 
              dataKey="desired" 
              name="Temps Souhaité" 
              fill="#82ca9d" 
              barSize={20}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default TimeDiscrepancyChart;